import React, { useCallback, useEffect, useState } from 'react';
import { Button, Link, Typography, makeStyles } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import { Launch, Warning } from '@material-ui/icons';
import { differenceInCalendarDays, format } from 'date-fns';
import { RouterLink } from 'components';
import { fetchTerms } from 'lib/Api/TermsGenerator';
import { TermsDocument } from 'lib/Model/TermsGenerator';
import { TermsDetailsDialog } from 'views/TermsGenerator/TermsDetailsDialog';
import {
  getTermsStatus,
  TERMS_STATUSES,
  TermsStatusChip,
} from 'views/TermsGenerator/TermsStatusChip';

// Agreements this close to expiry get a warning, so renewals are not missed
const EXPIRY_WARNING_DAYS = 60;

const useStyles = makeStyles((theme) => ({
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  row: {
    display: 'grid',
    gridTemplateColumns: '80px auto 1fr auto auto',
    alignItems: 'center',
    gap: theme.spacing(1),
    minHeight: 28,
  },
  brand: {
    fontWeight: theme.typography.fontWeightMedium,
  },
  dateText: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    color: theme.palette.text.secondary,
  },
  expiringSoon: {
    color: theme.palette.text.primary,
    fontWeight: theme.typography.fontWeightBold,
  },
  warningIcon: {
    fontSize: '1rem',
    color: theme.palette.warning.dark,
  },
  docLink: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    whiteSpace: 'nowrap',
  },
  linkIcon: {
    fontSize: '0.875rem',
  },
  showAll: {
    alignSelf: 'flex-start',
  },
}));

interface Props {
  companyId: string;
}

export const ClientTermsSummary: React.FC<Props> = ({ companyId }) => {
  const classes = useStyles();
  const [terms, setTerms] = useState<TermsDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [selectedTerm, setSelectedTerm] = useState<TermsDocument>();

  const loadTerms = useCallback(() => {
    setLoading(true);
    setError(false);
    fetchTerms({ companyId, page: 1, pageSize: 100 })
      .then((response) => setTerms(response.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [companyId]);

  useEffect(() => {
    loadTerms();
  }, [loadTerms]);

  const newestFirst = [...terms].sort(
    (a, b) =>
      new Date(b.date_of_issue).getTime() - new Date(a.date_of_issue).getTime(),
  );
  // A brand is covered when any of its terms is Active, so the best status
  // decides coverage. The sort is stable, so ties stay newest first.
  const bestFirst = [...newestFirst].sort(
    (a, b) =>
      TERMS_STATUSES.indexOf(getTermsStatus(a)) -
      TERMS_STATUSES.indexOf(getTermsStatus(b)),
  );
  const bestPerBrand = bestFirst.filter(
    (term, index) =>
      bestFirst.findIndex((t) => t.terms_brand === term.terms_brand) === index,
  );
  const visibleTerms = showAll ? newestFirst : bestPerBrand;

  const formatDate = (date: string) => format(new Date(date), 'd MMM yyyy');

  const renderDate = (term: TermsDocument, status: string) => {
    if (status === 'Expired' && term.terms_expiry_date) {
      return `Expired ${formatDate(term.terms_expiry_date)}`;
    }
    if (status === 'Not yet in effect' && term.signature_date) {
      return `Starts ${formatDate(term.signature_date)}`;
    }
    if (status === 'Active' || status === 'Paused') {
      if (!term.terms_expiry_date) {
        return 'No expiry date';
      }
      const daysLeft = differenceInCalendarDays(
        new Date(term.terms_expiry_date),
        new Date(),
      );
      if (daysLeft <= EXPIRY_WARNING_DAYS) {
        return (
          <span className={classes.expiringSoon}>
            Expires in {daysLeft} {daysLeft === 1 ? 'day' : 'days'}
          </span>
        );
      }
      return `Expires ${formatDate(term.terms_expiry_date)}`;
    }
    return `Issued ${formatDate(term.date_of_issue)}`;
  };

  const renderDocumentLink = (term: TermsDocument) => {
    let href = '';
    let label = '';
    if (term.terms_doc_id) {
      href = `https://drive.google.com/open?id=${term.terms_doc_id}`;
      label = 'Open document';
    } else if (term.folder_id) {
      href = `https://drive.google.com/drive/folders/${term.folder_id}`;
      label = 'Open folder';
    } else {
      return <span />;
    }
    return (
      <Link
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        variant="body2"
        className={classes.docLink}
      >
        {label}
        <Launch className={classes.linkIcon} />
      </Link>
    );
  };

  let content: React.ReactNode;
  if (loading && terms.length === 0) {
    content = (
      <>
        <Skeleton variant="text" width="90%" />
        <Skeleton variant="text" width="70%" />
      </>
    );
  } else if (error) {
    content = (
      <Typography variant="body2" color="error">
        Could not load terms.{' '}
        <Button size="small" color="primary" onClick={loadTerms}>
          Retry
        </Button>
      </Typography>
    );
  } else if (terms.length === 0) {
    content = (
      <Typography variant="body2" color="textSecondary">
        No terms issued through the intranet yet. Older agreements may not
        appear here.{' '}
        <Link component={RouterLink} to="/drive-terms">
          Generate terms
        </Link>
      </Typography>
    );
  } else {
    content = (
      <>
        {visibleTerms.map((term) => {
          const status = getTermsStatus(term);
          const date = renderDate(term, status);
          return (
            <div key={term.id} className={classes.row}>
              <Typography variant="body2" className={classes.brand} noWrap>
                {term.terms_brand}
              </Typography>
              <TermsStatusChip status={status} />
              <Typography variant="body2" className={classes.dateText}>
                {typeof date !== 'string' && (
                  <Warning className={classes.warningIcon} />
                )}
                {date}
              </Typography>
              {renderDocumentLink(term)}
              <Button
                size="small"
                color="primary"
                onClick={() => setSelectedTerm(term)}
              >
                Details
              </Button>
            </div>
          );
        })}
        {newestFirst.length > bestPerBrand.length && (
          <Button
            size="small"
            className={classes.showAll}
            onClick={() => setShowAll(!showAll)}
          >
            {showAll ? 'Show current only' : `Show all (${newestFirst.length})`}
          </Button>
        )}
      </>
    );
  }

  return (
    <div className={classes.list}>
      <Typography variant="caption">Terms of Business:</Typography>
      {content}
      <TermsDetailsDialog
        open={Boolean(selectedTerm)}
        onClose={() => setSelectedTerm(undefined)}
        term={selectedTerm}
        onSaved={loadTerms}
      />
    </div>
  );
};
